import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Parse a value coming from the API into a valid Date, or null.
 * Tolerates: ISO strings, epoch numbers, numeric strings, and
 * "YYYY-MM-DD HH:MM:SS" (Postgres/SQLite timestamps without a 'T',
 * which Safari/older engines fail to parse) by normalizing the space
 * to 'T'. Returns null for empty/invalid input instead of an
 * "Invalid Date" object.
 */
export function parseDate(value: unknown): Date | null {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;

  if (typeof value === 'number') {
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    // Pure numeric string -> epoch (ms or seconds).
    if (/^\d+$/.test(trimmed)) {
      const num = Number(trimmed);
      const ms = trimmed.length <= 10 ? num * 1000 : num;
      const d = new Date(ms);
      return isNaN(d.getTime()) ? null : d;
    }
    // Normalize "YYYY-MM-DD HH:MM:SS" -> "YYYY-MM-DDTHH:MM:SS".
    let normalized = trimmed;
    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(normalized)) {
      normalized = normalized.replace(' ', 'T');
    }
    const d = new Date(normalized);
    return isNaN(d.getTime()) ? null : d;
  }

  return null;
}

/**
 * Format a date value for display. Returns `fallback` (default '\u2014')
 * when the value is missing or unparseable, so the UI never shows
 * "Invalid Date".
 */
export function formatDate(
  value: unknown,
  options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' },
  locale = 'es-MX',
  fallback = '\u2014',
): string {
  const d = parseDate(value);
  if (!d) return fallback;
  return d.toLocaleDateString(locale, options);
}

/**
 * Format a date+time value for display, with the same safe fallback as
 * formatDate.
 */
export function formatDateTime(
  value: unknown,
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  },
  locale = 'es-MX',
  fallback = '\u2014',
): string {
  const d = parseDate(value);
  if (!d) return fallback;
  return d.toLocaleString(locale, options);
}

// force-reapply 1782311653
